import { format } from "date-fns";
import DebugService from "./DebugService";
import RecommendationApiService from "./RecommendationApiService";
import { v4 as uuid } from "uuid";

const OptimizationConstants = {
  CONST_RECOMMENDATION_OPTION : 'Recommended',
  CONST_CURRENT_SERVICE_CONFIGURATION : 'CSC_',
  CONST_SERVICE_RECOMMENDATION_OPTION : 'SRO_',
  CONST_CURRENT_CONFIGURATION : 'CC_',
  CONST_VOLUME_RECOMMENDATION_OPTION : 'VRO_',
  CONST_MEMORY_SIZE_RECOMMENDATION_OPTION : 'MSRO_',
  CONST_RECOMMENDATION_ACTION: 'RA_',
  CONST_LIFE_CYCLE_CONFIGURATION: 'LCC_'
}

const columnWidth = 200

/**
 * Formats value based on its type
 * @param {*} value 
 * @returns 
 */
const formatValue = (value) => {
  let newValue = value

  if ( [false, true].includes(value)) {
    // Type cast boolean values into string
    newValue = '' + value
  }

  return newValue
}

/**
 * Retrieves nested columns for requested data object
 * @param {*} prefix 
 * @param {*} data 
 * @returns 
 */
const getNestedColumns = (prefix, data) => {
  let result = []

  for (const key in data) {
    if(![
      'Filter', // S3
      'savingsOpportunityAfterDiscounts', // EBS
    ].includes(key)) {
      const newNestedColumn = {
        field: `${prefix}${key}`,
        headerName: `${prefix}${key[0].toLocaleUpperCase() + key.slice(1)}`,
        width: columnWidth,
        align: 'center',
        sortable: true
      }

      // In ASG
      if (key === 'configuration') {
        // ASG > recommendationOption > configuration > desiredCapacity
        if (data[key]['desiredCapacity'] !== undefined) {
          result = [...result, {
            field: `${prefix}desiredCapacity`,
            width: columnWidth,
            align: 'center',
            headerName: `RecommendedDesiredCapacity`,
            sortable: true
          }]    
        } 
        
        // ASG > recommendationOption > configuration > instanceType
        if (data[key]['instanceType'] !== undefined) {
          result = [...result, {
            field: `${prefix}instanceType`,
            width: columnWidth,
            align: 'center',
            headerName: `RecommendedInstanceType`,
            sortable: true
          }]    
        }
        
        // EC2
      } else if (key === 'projectedUtilizationMetrics') {
        result = [...result, {
          field: `${prefix}projectedUtilizationMetrics`,
          width: columnWidth,
          align: 'center',
          headerName: `PeakCPU%`,
          sortable: true
        }]
      } else {
        result = [...result, newNestedColumn]
      }

    }
  }

  return result
}

/**
 * @description Azure Optimization Service
 */
const AzureOptimizationService = {

  totalMonthlyCost: {},

  /**
   * Retrieves tabular data
   * @param {*} type 
   * @returns 
   */
  getTabularData(selectedAccount, type, userId) {
    console.log('getTabularData', selectedAccount, type, userId);
    const result = []

    return RecommendationApiService.getAzureData(selectedAccount, type, userId)
    .then(response => {
      let data = response['data']
      this.totalMonthlyCost = response['totalMonthlyCost'] ?? {}

      // Show only bucket list for S3 Optimization
      // bucket list is mapped to get desired column name list
      // if (type === 'S3') {
      //   data = (response['data']['Buckets']).map(d => {
      //     return {
      //       'bucket': d['Name'],
      //       'creationDate': format(d['CreationDate'], "yyyy-MM-dd")
      //     }
      //   })
      // }

      DebugService.log(`${type} data`, data)

      if (data.length === 0) {
        return {
          columns: [],
          rows: []
        }
      }

      // Fetch column name list
      let tableColumns = [];

      let firstRecord = data[0]

      // Find the data where LifecycleConfiguration is not empty as first record
      // which is required to find all column names
      if (type === 'S3') {
        firstRecord = data.find(d => d['LifecycleConfiguration'] !== undefined)
      }

      DebugService.log(`Optimization Service: Data first record`, firstRecord)

      for (const key in firstRecord) {
        if (![
          'instanceArn', // EC2
          'recommendationOption', // ASG, EC2
          'currentServiceConfiguration', // ECS
          'serviceArn', // ECS
          'serviceRecommendationOption', // ECS
          'currentConfiguration', // ASG, EBS
          'volumeArn', // EBS
          'accountId', // EBS
          'volumeRecommendationOption', // EBS
          'functionArn', // LAMBDA
          'memorySizeRecommendationOption', // LAMBDA
          'autoScalingGroupArn', // ASG
          'ResourceArn', // RDS
          'RecommendedAction', // RDS,
          'Instances', // ASG
          'Tags', // ASG,
          'AccountId', // EBS Snapshot
          'OwnerId', // EBS Snapshot
          'Progress', // EBS Snapshot
          'Description', // EBS Snapshot
          'StartTimeEnum', // EBS Snapshot
          'Tags', // EBS Snapshot
          'StorageTier', // EBS Snapshot
          'Encrypted', // EBS Snapshot
        ].includes(key)) {
          const newColumn = {
            field: key,
            headerName: key[0].toLocaleUpperCase() + key.slice(1),
            width: columnWidth,
            align: 'center',
            sortable: true
          }

          // S3
          if (key === 'BucketName') {
            newColumn.headerName = 'Bucket'
          }
          if (key === 'BucketCreationDate') {
            newColumn.headerName = 'CreationDate'
          }
          if (key === 'BucketSize') {
            newColumn.headerName = 'Size'
          }
          if (key === 'LifecycleConfiguration') {
            newColumn.headerName = 'LifecyclePolicy'
          }
          if (key === 'StandardS3CostValue') {
            newColumn.headerName = 'StandardS3Cost'
          }
          if (key === 'InfrequentAccessCostValue') {
            newColumn.headerName = 'InfrequentAccessCost'
          }
          if (key === 'GlacierCostValue') {
            newColumn.headerName = 'GlacierCost'
          }
          if (key === 'S3VersioningStatus') {
            newColumn.headerName = 'Status'
          }

          // ASG
          if (['autoScalingGroupName', 'AutoScalingGroupName'].includes(key)) {
            newColumn.headerName = 'ASG Name'
          }
          if (key === 'AutoScalingGroupARN') {
            newColumn.headerName = 'ASG ARN'
          }

          // EC2
          if (key === 'platformDetails') {
            newColumn.headerName = 'Platform'
          }
          if (key === 'privateIpAddress') {
            newColumn.headerName = 'IPv4'
          }
          if (key === 'stateName') {
            newColumn.headerName = 'State'
          }
          if (key === 'vpcId') {
            newColumn.headerName = 'VPCID'
          }
          if (key === 'imageId') {
            newColumn.headerName = 'AMIId'
          }
          if (key === 'currentVcpu') {
            newColumn.headerName = 'Current-vCPU'
          }
          if (key === 'recommendedVcpu') {
            newColumn.headerName = 'Recommended-vCPU'
          }
          if (key === 'priceValueDifference') {
            newColumn.headerName = 'PriceDifference'
          }
          if (key === 'priceValueDifferenceMonthly') {
            newColumn.headerName = 'MonthlyPriceDifference'
          }
          if (key === 'averageCPUValue') {
            newColumn.headerName = 'AvgCPU%(14 Days)'
          }
          if (key === 'sumNetworkInByteValue') {
            newColumn.headerName = 'SumNetworkInByte(14 Days)'
          }
          if (key === 'sumNetworkOutByteValue') {
            newColumn.headerName = 'SumNetworkOutByte(14 Days)'
          }
          if (key === 'customRecomendationRemark') {
            newColumn.headerName = 'Remark(s)'
          }
          
          // RDS
          if (key === 'DBInstanceIdentifier') {
            newColumn.headerName = 'Instance_Identifier'
          }
          if (key === 'DBClusterIdentifier') {
            newColumn.headerName = 'Cluster_Identifier'
          }
          if (key === 'DBInstanceStatus') {
            newColumn.headerName = 'InstanceState'
          }
          if (key === 'DBInstanceClass') {
            newColumn.headerName = 'InstanceClass'
          }
          if (key === 'LicenseModel') {
            newColumn.headerName = 'License'
          }
          if (key === 'EndpointAddress') {
            newColumn.headerName = 'RDS_Endpoint'
          }

          if ([
            'InstanceCreateTime', // RDS
            'createTime' // EBS
          ].includes(key)) {
            newColumn.headerName = 'CreateDate'
          }
          
          // EBS
          if (key === 'attachmentIntanceIds') {
            newColumn.headerName = 'InstanceId'
          }
          if (type === 'EBS' && key === 'size') {
            newColumn.headerName = 'Size(GB)'
          }
          if (type === 'EBS' && key === 'currentOnDemandMonthlyStoragePriceValue') {
            newColumn.headerName = 'CurrentMonthlyStorageCost($)'
          }
          if (type === 'EBS' && key === 'recommendedOnDemandMonthlyStoragePriceValue') {
            newColumn.headerName = 'RecommendedMonthlyStorageCost($)'
          }
          if (type === 'EBS' && key === 'monthlyStoragePriceValueDifference') {
            newColumn.headerName = 'MonthlyStorageCostDifference($)'
          }
          if (type === 'EBS' && key === 'volumeId') {
            // This is required to make mui table to have unique id
            newColumn.field = 'id'
          }
          
          // LAMBDA
          if (type === 'LAMBDA' && key === 'runtime') {
            newColumn.headerName = 'RuntimeEnvironment'
          }
          if (type === 'LAMBDA' && key === 'timeout') {
            newColumn.headerName = 'FunctionTimeout'
          }
          
          // Elastic IP
          if (type === 'CUSTOM_ELASTIC_IP' && key === 'AllocationId') {
            newColumn.headerName = 'Location ID'
          }
          if (type === 'CUSTOM_ELASTIC_IP' && key === 'PublicIp') {
            newColumn.headerName = 'Public IP'
          }
          if (type === 'CUSTOM_ELASTIC_IP' && key === 'AssociationId') {
            newColumn.headerName = 'Association ID'
          }
          if (type === 'CUSTOM_ELASTIC_IP' && key === 'NetworkInterfaceId') {
            newColumn.headerName = 'Network Interface ID'
          }
          if (type === 'CUSTOM_ELASTIC_IP' && key === 'PrivateIpAddress') {
            newColumn.headerName = 'Private IP'
          }
          if (type === 'CUSTOM_ELASTIC_IP' && key === 'PublicIpv4Pool') {
            newColumn.headerName = 'IPV4'
          }
          if (type === 'CUSTOM_ELASTIC_IP' && key === 'CustomRecomendationRemark') {
            newColumn.headerName = 'Remark'
          }
          if (type === 'CUSTOM_ELASTIC_IP' && key === 'CustomRecommendedOnDemandMonthlyPriceValue') {
            newColumn.headerName = 'RecommendedOnDemandMonthlyPriceValue'
          }

          // EBS & RDS Snapshot
          if (['CUSTOM_EBS_SNAPSHOT', 'CUSTOM_RDS_SNAPSHOT'].includes(type) && key === 'StartMonth') {
            newColumn.headerName = 'Backup'
          }

          tableColumns = [...tableColumns, newColumn]
        }

        // Fetch column names for recommendationOption which is an object type
        if (key === 'recommendationOption') {
          const extraColumns = getNestedColumns(OptimizationConstants.CONST_RECOMMENDATION_OPTION, firstRecord['recommendationOption'])

          if (extraColumns.length > 0) {
            tableColumns = [...tableColumns, ...extraColumns]
          }
        }

        // Fetch column names for currentServiceConfiguration which is an object type
        if (key === 'currentServiceConfiguration') {
          const extraColumns = getNestedColumns(OptimizationConstants.CONST_CURRENT_SERVICE_CONFIGURATION, firstRecord['currentServiceConfiguration'])

          if (extraColumns.length > 0) {
            tableColumns = [...tableColumns, ...extraColumns]
          }
        }

        // Fetch column names for serviceRecommendationOption which is an object type
        if (key === 'serviceRecommendationOption') {
          const extraColumns = getNestedColumns(OptimizationConstants.CONST_SERVICE_RECOMMENDATION_OPTION, firstRecord['serviceRecommendationOption'])

          if (extraColumns.length > 0) {
            tableColumns = [...tableColumns, ...extraColumns]
          }
        }

        // Fetch column names for currentConfiguration which is an object type
        if (key === 'currentConfiguration') {
          const extraColumns = getNestedColumns('', firstRecord['currentConfiguration'])

          if (extraColumns.length > 0) {
            tableColumns = [...tableColumns, ...extraColumns]
          }
        }

        // Fetch column names for volumeRecommendationOption which is an object type
        if (key === 'volumeRecommendationOption') {
          const extraColumns = getNestedColumns(OptimizationConstants.CONST_VOLUME_RECOMMENDATION_OPTION, firstRecord['volumeRecommendationOption'])

          if (extraColumns.length > 0) {
            tableColumns = [...tableColumns, ...extraColumns]
          }
        }

        // Fetch column names for memorySizeRecommendationOption which is an object type
        if (key === 'memorySizeRecommendationOption') {
          const extraColumns = getNestedColumns(OptimizationConstants.CONST_MEMORY_SIZE_RECOMMENDATION_OPTION, firstRecord['memorySizeRecommendationOption'])

          if (extraColumns.length > 0) {
            tableColumns = [...tableColumns, ...extraColumns]
          }
        }

        // Fetch column names for recommendationAction which is an object type
        if (key === 'RecommendedAction') {
          const extraColumns = getNestedColumns(OptimizationConstants.CONST_RECOMMENDATION_ACTION, firstRecord['RecommendedAction'])

          if (extraColumns.length > 0) {
            tableColumns = [...tableColumns, ...extraColumns]
          }
        }

        // Fetch column names for LifecycleConfiguration which is an object type
        // S3 type
        // [Removed as not required]
        // if (key === 'LifecycleConfiguration') {
        //   const extraColumns = getNestedColumns(OptimizationConstants.CONST_LIFE_CYCLE_CONFIGURATION, firstRecord['LifecycleConfiguration'][0])

        //   if (extraColumns.length > 0) {
        //     tableColumns = [...tableColumns, ...extraColumns]
        //   }
        // }
      }

      DebugService.log(`Optimization Service: Table column name list`, tableColumns)

      // Add fetched column name list to the result
      result['columns'] = tableColumns

      // Fetch column data list
      let tableRows = []

      for (let i=0; i < data.length; i++) {
        const rowData = {}
        rowData['id'] = uuid()

        for (const key in data[i]) {
          if (![
            'recommendationOption', // ASG, EC2
            'currentServiceConfiguration', // ECS
            'serviceArn', // ECS
            'serviceRecommendationOption', // ECS
            'currentConfiguration', // ASG, EBS
            'volumeRecommendationOption', // EBS
            'attachmentIntanceIds', // EBS
            'memorySizeRecommendationOption', // LMABDA
            'instanceArn', // EC2
            'volumeArn', // EBS
            'functionArn', // LAMBDA
            'autoScalingGroupArn', // ASG
            'ResourceArn', // RDS
            'Links', // RDS
            'RecommendedAction', //RDS
            'LifecycleConfiguration' // S3
          ].includes(key)) {
            rowData[key] = formatValue(data[i][key])

            // Format date values
            if ([
              'createdtime',
              'bucketcreationdate',
              'instancecreatetime',
              'updatedtime',
              'lastmodified',
              'createtime',
              'launchtime'
            ].includes(key.toLocaleLowerCase())) {
              rowData[key] = (new Date(data[i][key])).toDateString()
            }
          }

          // Fetch column data for recommendationOption which is an object type
          if ([
            'recommendationOption',
          ].includes(key)) {
            for (const keyX in data[i][key]) {
              const customKey = `${OptimizationConstants.CONST_RECOMMENDATION_OPTION}${keyX}`

              if (![
                // 'projectedUtilizationMetrics', // ASG, EC2
                'savingsOpportunity', // ASG, EC2
                'savingsOpportunityAfterDiscounts', // ASG, EC2
                'configuration', // ASG
              ].includes(keyX)) {
                rowData[customKey] = data[i][key][keyX]
              }

              // ASG > recommendationOption > configuration > desiredCapacity
              if (keyX === 'configuration') {
                rowData['RecommendeddesiredCapacity'] = data[i][key][keyX]['desiredCapacity'] 
              }

              // ASG > recommendationOption > configuration > instanceType
              if (keyX === 'configuration') {
                rowData['RecommendedinstanceType'] = data[i][key][keyX]['instanceType'] 
              }

              // EC2 > recommendationOption > projectedUtilizationMetrics > value
              if (keyX === 'projectedUtilizationMetrics') {
                rowData[customKey] = data[i][key][keyX][0]['value']
              }
            }
          }
          
          // Fetch column data for currentServiceConfiguration which is an object type
          if ([
            'currentServiceConfiguration',
          ].includes(key)) {
            for (const keyX in data[i][key]) {
              const customKey = `${OptimizationConstants.CONST_CURRENT_SERVICE_CONFIGURATION}${keyX}`

              if (![
                'containerConfigurations', // ECS
              ].includes(keyX)) {
                rowData[customKey] = data[i][key][keyX]
              }
            }
          }

          // Fetch column data for serviceRecommendationOption which is an object type
          if ([
            'serviceRecommendationOption',
          ].includes(key)) {
            for (const keyX in data[i][key]) {
              const customKey = `${OptimizationConstants.CONST_SERVICE_RECOMMENDATION_OPTION}${keyX}`

              if (![
                'containerRecommendations',
                'projectedUtilizationMetrics',
                'savingsOpportunity',
                'savingsOpportunityAfterDiscounts'
              ].includes(keyX)) {
                rowData[customKey] = data[i][key][keyX]
              }
            }
          }

          // Fetch column data for currentConfiguration which is an object type
          if ([
            'currentConfiguration',
          ].includes(key)) {
            for (const keyX in data[i][key]) {
              const customKey = `${keyX}`

              if (![].includes(keyX)) {
                rowData[customKey] = formatValue(data[i][key][keyX])
              }
            }
          }

          // Fetch column data for volumeRecommendationOption which is an object type
          // EBS
          if ([
            'volumeRecommendationOption',
          ].includes(key)) {
            for (const keyX in data[i][key]) {
              const customKey = `${OptimizationConstants.CONST_VOLUME_RECOMMENDATION_OPTION}${keyX}`

              if (![
                'configuration', // EBS
                'savingsOpportunity', // EBS
                'savingsOpportunityAfterDiscounts' //EBS
              ].includes(keyX)) {
                rowData[customKey] = data[i][key][keyX]
              }

              if (keyX === 'savingsOpportunity') {
                rowData[customKey] = `${data[i][key][keyX]['estimatedMonthlySavings']['value']}$`
              }
            }
          }

          // Fetch column data for memorySizeRecommendationOption which is an object type
          if ([
            'memorySizeRecommendationOption',
          ].includes(key)) {
            for (const keyX in data[i][key]) {
              const customKey = `${OptimizationConstants.CONST_MEMORY_SIZE_RECOMMENDATION_OPTION}${keyX}`

              if (![
                'projectedUtilizationMetrics' // LAMBDA
              ].includes(keyX)) {
                rowData[customKey] = data[i][key][keyX]
              }
            }
          }

          // Fetch column data for RecommendedAction which is an object type
          if ([
            'RecommendedAction',
          ].includes(key)) {
            for (const keyX in data[i][key]) {
              const customKey = `${OptimizationConstants.CONST_RECOMMENDATION_ACTION}${keyX}`

              if (![
                'ContextAttributes', // RDS
                'Parameters' // RDS
              ].includes(keyX)) {
                rowData[customKey] = data[i][key][keyX]

                // if (Array.isArray(data[i][key][keyX]) && data[i][key][keyX].length < 1) {
                //   rowData[customKey] = []
                // }
              } else {
                
              }
            }
          }

          // Fetch column data for LifecycleConfiguration which is an object type
          if ([
            'LifecycleConfiguration',
          ].includes(key)) {
            rowData[key] = data[i].length > 0 ? 'true' : 'false'

            // for (const keyX in data[i][key][0]) {
            //   const customKey = `${OptimizationConstants.CONST_LIFE_CYCLE_CONFIGURATION}${keyX}`

            //   if (keyX === 'Expiration') {
            //     rowData[customKey] = `${data[i][key][0][keyX]['Days']} Days`
            //   } else {
            //     if (![
            //       'Filter'
            //     ].includes(keyX)) {
            //       rowData[customKey] = data[i][key][0][keyX]
            //     }
            //   }
            // }
          }

          // Fetch column data for attachmentIntanceIds which is an array type
          // and only one value is to be displayed
          if ([
            'attachmentIntanceIds',
          ].includes(key)) {
            rowData[key] = data[i][key][0]
          }
          
          if ([
            'StartMonth',
          ].includes(key)) {
            rowData[key] = `${data[i][key]} Month(s)`
          }

          // Prefix all price values with $ symbol
          // TODO: Fix for EC2 and CUTOM_EC2 (Heirarchy)
          if ([
            'CurrentOnDemandMonthlyPriceValue',
            'CustomRecommendedOnDemandMonthlyPriceValue',
            'CurrentOnDemandPriceValue',
            'CustomRecommendedOnDemandPriceValue',
            'StandardS3MonthlyCostValue',
            'currentInstanceCostValue',
            'currentInstanceMonthlyCostValue',
            'recommendedInstanceCostValue',
            'recommendedInstanceMonthlyCostValue',
            'StorageMonthlyCostValue',
            'CurrentInstanceCostValue',
            'CurrentInstanceMonthlyCostValue',
            'currentOnDemandMonthlyStoragePriceValue',
            'recommendedOnDemandMonthlyStoragePriceValue',
            'OnDemandMonthlyStoragePriceValue',
            'customOnDemandMonthlyStoragePriceValue',
            'currentOnDemandPriceValue',
            'currentOnDemandMonthlyPriceValue',
            'customRecommendedOnDemandPriceValue',
            'customRecommendedOnDemandMonthlyPriceValue',
            'recommendedOnDemandPriceValue',
            'recommendedOnDemandMonthlyPriceValue',
            'priceValueDifference',
            'priceValueDifferenceMonthly',
            'OnDemandPriceValue'
          ].includes(key) 
          && !['EC2', 'CUSTOM_EC2'].includes(type)) {
            rowData[key] = `$${(data[i][key] ?? 0).toFixed(2)}`
          }

          if ([
            'S3VersioningStatus',
          ].includes(key)) {
            rowData[key] = (data[i][key] === 'NotEnabled') ? 'Disabled' : 'Enabled'
          }
        }

        tableRows = [...tableRows, rowData]
      }

      DebugService.log(`Optimization Service: Table column data`, tableRows)

      // Add fetched column data to the result
      result['rows'] = tableRows
      result['totalMonthlyCost'] = this.totalMonthlyCost
      return result

    })
    .catch(e => {
      DebugService.error(`Optimization Service: Caught error while fetching ${type} data`)
    })
  },

  getTotalMonthlyCost() {
    return this.totalMonthlyCost ?? {}
  }
};

export default AzureOptimizationService;
